import React from 'react'
import { isSection, optionChoices } from './fieldTypes'
import { buildTree } from './StructurePanel'
import { Group, Row, Segmented } from './ui'
import { TypeIcon } from './TypePicker'
import { hasCondition } from '../conditions'
import { interpolate, useT } from './translations'

const NOTHING_TO_ANSWER = ['static', 'page_break']
const CHOICE_TYPES = ['select', 'select_prefill', 'yes_no', 'checkbox']
const NUMBER_TYPES = ['number', 'float']
// Dates are written differently by the web form, the app and the server:
// they are only tested for being answered
const ANSWER_ONLY = ['photos', 'e_sign', 'table', 'date', 'date_time']

export const operatorsFor = (source) => {
  if (!source) { return [] }
  if (ANSWER_ONLY.includes(source.data_type)) { return ['answered', 'not_answered'] }
  if (NUMBER_TYPES.includes(source.data_type)) { return ['greater_than', 'less_than', 'is', 'is_not', 'answered', 'not_answered'] }
  return ['is', 'is_not', 'answered', 'not_answered']
}

// A field only waits on an answer asked before it
export const sourcesFor = (target, fields) => {
  const tree = buildTree(fields)
  const ordered = tree.roots.flatMap((root) => (isSection(root) ? [root, ...(tree.children[root.id] || [])] : [root]))
  const index = ordered.findIndex((field) => field.id === target.id)
  const before = index < 0 ? ordered : ordered.slice(0, index)
  return before.filter((field) => !isSection(field) && !NOTHING_TO_ANSWER.includes(field.data_type) && field.section_id !== (isSection(target) ? target.id : -1))
}

export const conditionSummary = (condition, fields, t) => {
  if (!hasCondition(condition)) { return null }

  const source = fields.find((field) => field.id === condition.field_id)
  if (!source) { return null }

  const labels = Object.fromEntries(optionChoices(source, t))
  const values = (condition.values || []).map((value) => labels[value] || value).join(t.or_separator)
  return interpolate(t.condition_summary, { field: source.name, operator: t.operators[condition.operator], values }).trim()
}

const ConditionEditor = ({ target, fields, readOnly, onChange }) => {
  const t = useT()
  const condition = target.visible_when || {}
  const enabled = hasCondition(condition)
  const sources = sourcesFor(target, fields)
  const source = enabled && fields.find((field) => field.id === condition.field_id)
  const operators = operatorsFor(source)
  const choices = source ? optionChoices(source, t) : []
  const needsValues = enabled && !['answered', 'not_answered'].includes(condition.operator)

  const set = (changes) => onChange({ ...condition, ...changes })

  const pickSource = (fieldId) => {
    const next = fields.find((field) => field.id === parseInt(fieldId, 10))
    if (!next) { return }
    const nextOperators = operatorsFor(next)
    onChange({ field_id: next.id, operator: nextOperators.includes(condition.operator) ? condition.operator : nextOperators[0], values: [] })
  }

  const toggleValue = (value) => {
    const values = condition.values || []
    set({ values: values.includes(value) ? values.filter((item) => item !== value) : [...values, value] })
  }

  const start = () => {
    const first = sources[sources.length - 1]
    if (first) { onChange({ field_id: first.id, operator: operatorsFor(first)[0], values: [] }) }
  }

  return (
    <Group title={t.visibility}>
      <Segmented
        value={enabled ? 'conditional' : 'always'}
        disabled={readOnly || (!enabled && sources.length === 0)}
        options={[['always', t.always_shown], ['conditional', <><i className="fas fa-code-branch" /> {t.shown_if}</>]]}
        onChange={(value) => (value === 'always' ? onChange({}) : start())}
      />
      {!enabled && sources.length === 0 && <div className="fb-prop-hint">{t.no_condition_sources}</div>}

      {enabled &&
        <div className="fb-condition">
          <Row label={t.condition_source}>
            <select className="fb-input" value={condition.field_id || ''} disabled={readOnly} onChange={(event) => pickSource(event.target.value)}>
              {!source && <option value="">{t.condition_missing_source}</option>}
              {sources.map((field) => <option key={field.id} value={field.id}>{field.name || t.untitled}</option>)}
            </select>
          </Row>

          {source &&
            <Row label={t.condition_operator}>
              <select className="fb-input" value={condition.operator} disabled={readOnly} onChange={(event) => set({ operator: event.target.value })}>
                {operators.map((operator) => <option key={operator} value={operator}>{t.operators[operator]}</option>)}
              </select>
            </Row>
          }

          {source && needsValues && (CHOICE_TYPES.includes(source.data_type) && !['greater_than', 'less_than'].includes(condition.operator)
            ? (
              <Row label={t.condition_values} hint={t.condition_values_hint}>
                <div className="fb-chips">
                  {choices.map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      disabled={readOnly}
                      className={`fb-choice-chip${(condition.values || []).includes(value) ? ' active' : ''}`}
                      onClick={() => toggleValue(value)}
                    >
                      {(condition.values || []).includes(value) && <i className="fas fa-check" />} {label}
                    </button>
                  ))}
                </div>
              </Row>
            )
            : (
              <Row label={t.condition_value}>
                <input
                  className="fb-input"
                  type={NUMBER_TYPES.includes(source.data_type) ? 'number' : 'text'}
                  step="any"
                  value={(condition.values || [])[0] || ''}
                  disabled={readOnly}
                  onChange={(event) => set({ values: event.target.value === '' ? [] : [event.target.value] })}
                />
              </Row>
            )
          )}

          {source &&
            <div className="fb-condition-summary">
              <TypeIcon type={source.data_type} small />
              <span>{conditionSummary(condition, fields, t) || t.condition_incomplete}</span>
            </div>
          }
        </div>
      }
    </Group>
  )
}

export default ConditionEditor
